import { WORDPROCESSINGML_NS } from "./ooxml-names";

export interface RevisionConfig {
    author: string;
    /** ISO timestamp written as `w:date`; every change of one redline shares it, which is how Apply recognises its own revisions later. */
    stampedAt: string;
    firstAllocatedRevisionId: number;
}

export class Revision {
    readonly author: string;
    readonly stampedAt: string;
    private nextAvailableRevisionId: number;

    constructor(config: RevisionConfig) {
        this.author = config.author;
        this.stampedAt = config.stampedAt;
        this.nextAvailableRevisionId = config.firstAllocatedRevisionId;
    }

    /**
     * Word and Word Online do not agree on author capitalisation or spacing,
     * and the same person can appear as "james philhower" and "James  Philhower"
     * in one document. Every authorship decision in the engine and the proposal
     * treats those as the same author, so this is the one comparison they use.
     */
    static sameAuthor(knownAuthor: string | null | undefined, candidateAuthor: string | null | undefined): boolean {
        return knownAuthor != null && candidateAuthor != null && Revision.authorKey(knownAuthor) === Revision.authorKey(candidateAuthor);
    }

    /**
     * The one identity of an author across Word's spellings: trimmed, lowered,
     * inner whitespace collapsed to one space. Everything that keys on an
     * author, the preview's colour and the legend's rows included, keys on
     * this, so "james philhower" and "James  Philhower" are one person
     * everywhere they are shown.
     */
    static authorKey(author: string): string {
        return author.trim().toLowerCase().replace(/\s+/g, " ");
    }

    /** Hands out consecutive ids; a split revision container takes the next id so the two halves stay distinct revisions. */
    takeNextRevisionId(): string {
        /**
         * RETURNS
         *   The current revision id as a string, before incrementing the counter.
         *
         * EXAMPLE
         *   `firstAllocatedRevisionId` = 12
         *   first call                 -> returns `"12"`
         *   state after first call     -> next id is 13
         *   second call                -> returns `"13"`
         */
        return String(this.nextAvailableRevisionId++);
    }

    /**
     * Writes the three attributes Word requires on `w:ins`, `w:del` and their
     * relatives. The id is unique within the paragraph being redlined (see the
     * header); author and date are the same on every element this revision
     * stamps, so a reader can group them as one change.
     */
    stamp(element: Element): Element {
        element.setAttributeNS(WORDPROCESSINGML_NS, "w:id", this.takeNextRevisionId());
        element.setAttributeNS(WORDPROCESSINGML_NS, "w:author", this.author);
        element.setAttributeNS(WORDPROCESSINGML_NS, "w:date", this.stampedAt);
        return element;
    }
/**
 * SCOPE
 *   One redline's revision identity and its `w:id` source.
 *
 * CASES
 *   - edits inside one paragraph      -> `firstAllocatedRevisionId` is `FIRST_REVISION_ID`
 *   - whole inserted paragraphs       -> `firstAllocatedRevisionId` is
 *                                         `ProposalBuilder.insertedParagraphRevision`
 *
 * WHY
 *   `takeNextRevisionId()` returns the current value, then increments.
 *
 * EXAMPLE
 *   `firstAllocatedRevisionId` = 12
 *   first stamped element      -> `w:id="12"`
 *   next stamped element       -> `w:id="13"`
 */

    /** Whether a revision element in the document was written by this revision's author, spelling variance included. */
    isBy(author: string | null | undefined): boolean {
        return Revision.sameAuthor(author, this.author);
    }
}
